import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CalendarDays,
  ChevronRight,
  Church,
  Clock3,
  ExternalLink,
  HeartHandshake,
  Mail,
  MapPin,
  Menu,
  Quote,
  Radio,
  Sparkles,
  UsersRound,
  X,
} from 'lucide-react';
import './App.css';

const assets = {
  logo: new URL('../public/iba-logo.png', import.meta.url).toString(),
  icon: new URL('../public/Adhoreb-icon.png', import.meta.url).toString(),
  church: new URL('../public/assets/Lugar Iglesia.jpg', import.meta.url).toString(),
  worship: new URL('../public/assets/image-1.jpeg', import.meta.url).toString(),
  childrenEvent: new URL('../public/assets/image-2.jpg', import.meta.url).toString(),
  congregationFront: new URL('../public/assets/image-4.jpeg', import.meta.url).toString(),
  congregationWide: new URL('../public/assets/image-5.jpeg', import.meta.url).toString(),
  prayer: new URL('../public/assets/image-6.jpeg', import.meta.url).toString(),
  missions: new URL('../public/assets/image-7-missions.jpeg', import.meta.url).toString(),
  sundayTeaching: new URL('../public/assets/image-8-escuela-dominical.jpeg', import.meta.url).toString(),
  sundayPrayer: new URL('../public/assets/image-9-escuela-dominical.jpeg', import.meta.url).toString(),
  sundayActivities: new URL('../public/assets/image-10-escuela-dominical.jpeg', import.meta.url).toString(),
  babyPresentation: new URL('../public/assets/image-11-baby-presentation.jpeg', import.meta.url).toString(),
  pastors: new URL('../public/assets/pastors.jpeg', import.meta.url).toString(),
};

const navLinks = [
  { label: 'Inicio', href: '#inicio' },
  { label: 'Nosotros', href: '#nosotros' },
  { label: 'Servicios', href: '#servicios' },
  { label: 'Ministerios', href: '#ministerios' },
  { label: 'Pastores', href: '#pastores' },
  { label: 'Galería', href: '#galeria' },
  { label: 'Visítanos', href: '#visitanos' },
];

const serviceCards = [
  {
    icon: BookOpen,
    kicker: 'Cada miércoles',
    title: 'Estudio bíblico',
    time: '6:30 p. m.',
    description:
      'Un espacio para crecer juntos en el conocimiento y la aplicación de la Palabra de Dios.',
    note: 'El primer miércoles del mes dedicamos este horario exclusivamente a la oración.',
  },
  {
    icon: Church,
    kicker: 'Cada domingo',
    title: 'Servicio congregacional',
    time: '10:00 a. m. — 12:00 p. m.',
    description:
      'Nos reunimos como familia para adorar, escuchar la Palabra y disfrutar de comunión cristiana.',
    note: 'La escuela dominical para niños se desarrolla simultáneamente durante el servicio.',
  },
];

const ministries = [
  {
    title: 'Escuela dominical',
    description:
      'Un ambiente alegre y seguro donde nuestros niños conocen a Jesús y aprenden verdades bíblicas para toda la vida.',
    image: assets.sundayActivities,
    alt: 'Niños participando en una actividad de la escuela dominical',
    icon: UsersRound,
  },
  {
    title: 'Misiones',
    description:
      'Servimos con alegría y compartimos el evangelio, dentro y fuera de nuestra comunidad, haciendo visible el amor de Cristo.',
    image: assets.missions,
    alt: 'Grupo de participantes de una actividad misionera en la iglesia',
    icon: HeartHandshake,
  },
  {
    title: 'Familias y comunidad',
    description:
      'Celebramos cada etapa de la vida y acompañamos a las familias mientras crecen en fe, gracia y comunión.',
    image: assets.babyPresentation,
    alt: 'Presentación de un bebé junto a su familia en la iglesia',
    icon: Sparkles,
  },
];

const galleryImages = [
  {
    src: assets.childrenEvent,
    alt: 'Niños reunidos durante una actividad especial de la iglesia',
    caption: 'Una iglesia para cada generación',
  },
  {
    src: assets.congregationFront,
    alt: 'Congregación y visitantes reunidos frente al púlpito',
    caption: 'Creciendo juntos en la Palabra',
  },
  {
    src: assets.prayer,
    alt: 'Personas orando juntas durante un servicio',
    caption: 'Una comunidad que ora',
  },
  {
    src: assets.sundayTeaching,
    alt: 'Maestra guiando a los niños en la escuela dominical',
    caption: 'Enseñando con amor',
  },
  {
    src: assets.sundayPrayer,
    alt: 'Niños orando durante la escuela dominical',
    caption: 'Formando vidas en Cristo',
  },
];

const mapUrl = 'https://share.google/UoChEmzDPylaQ5BI9';
const facebookUrl = 'https://www.facebook.com/iglesia.bautistaadhoreb';

export function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const closeMenu = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', closeMenu);
    return () => window.removeEventListener('keydown', closeMenu);
  }, []);

  const openGalleryImage = (index: number) => {
    setSelectedImage(index);
    requestAnimationFrame(() => dialogRef.current?.showModal());
  };

  const changeGalleryImage = (direction: number) => {
    if (selectedImage === null) return;
    setSelectedImage(
      (selectedImage + direction + galleryImages.length) % galleryImages.length,
    );
  };

  return (
    <>
      <a className="skip-link" href="#contenido">
        Saltar al contenido
      </a>

      <header className="site-header">
        <div className="header-inner shell">
          <a className="brand" href="#inicio" aria-label="Iglesia Bautista Adhoreb, inicio">
            <img src={assets.logo} alt="Iglesia Bautista Adhoreb" />
          </a>

          <button
            className="menu-button"
            type="button"
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
            aria-controls="main-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>

          <nav
            id="main-navigation"
            className={`main-nav ${menuOpen ? 'is-open' : ''}`}
            aria-label="Navegación principal"
          >
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} onClick={() => setMenuOpen(false)}>
                {link.label}
              </a>
            ))}
          </nav>

          <a className="header-cta" href="#servicios">
            Planifica tu visita
            <ChevronRight size={17} aria-hidden="true" />
          </a>
        </div>
      </header>

      <main id="contenido">
        <section className="hero" id="inicio">
          <div className="hero-glow hero-glow-one" aria-hidden="true" />
          <div className="hero-glow hero-glow-two" aria-hidden="true" />
          <div className="hero-inner shell">
            <div className="hero-copy">
              <p className="eyebrow eyebrow-light">
                <span /> Iglesia Bautista Adhoreb
              </p>
              <h1>
                Una iglesia bíblica, <em>cristocéntrica</em> y para toda la familia.
              </h1>
              <p className="hero-description">
                Un lugar para conocer a Cristo, crecer en su Palabra y caminar en comunidad.
                Hay un espacio para ti y tu familia.
              </p>
              <div className="hero-actions">
                <a className="button button-primary" href="#servicios">
                  Únete este domingo
                  <ArrowRight size={19} aria-hidden="true" />
                </a>
                <a
                  className="button button-secondary"
                  href={mapUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Cómo llegar
                  <MapPin size={18} aria-hidden="true" />
                </a>
                <a
                  className="button button-secondary"
                  href={facebookUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Ver transmisiones
                  <Radio size={18} aria-hidden="true" />
                </a>
              </div>
              <div className="hero-times" aria-label="Próximos horarios regulares">
                <div>
                  <CalendarDays aria-hidden="true" />
                  <span><strong>Domingos</strong> 10:00 a. m.</span>
                </div>
                <div>
                  <Clock3 aria-hidden="true" />
                  <span><strong>Miércoles</strong> 6:30 p. m.</span>
                </div>
              </div>
            </div>

            <div className="hero-visual" aria-label="Nuestra iglesia y congregación">
              <div className="hero-photo hero-photo-main">
                <img src={assets.church} alt="Fachada de la Iglesia Bautista Adhoreb" />
              </div>
              <div className="hero-photo hero-photo-float">
                <img src={assets.congregationWide} alt="Congregación reunida durante un servicio dominical" />
              </div>
              <div className="hero-location-card">
                <MapPin aria-hidden="true" />
                <span><strong>San Cristóbal, R. D.</strong> Modesto Díaz #5</span>
              </div>
              <div className="hero-cross" aria-hidden="true">✦</div>
            </div>
          </div>
          <div className="hero-bottom shell" aria-hidden="true">
            <span>Fe</span><i /> <span>Palabra</span><i /> <span>Comunidad</span>
          </div>
        </section>

        <section className="about section" id="nosotros">
          <div className="shell about-grid">
            <div className="about-image-wrap reveal-block">
              <img
                className="about-image"
                src={assets.worship}
                alt="La Iglesia Bautista Adhoreb adorando durante un servicio"
                loading="lazy"
              />
              <div className="about-badge">
                <Church aria-hidden="true" />
                <span><strong>IBA</strong> Una familia en Cristo</span>
              </div>
            </div>
            <div className="about-copy">
              <p className="eyebrow">Quiénes somos</p>
              <h2>Una comunidad formada por la Palabra y centrada en Jesús.</h2>
              <p className="lead">
                En Adhoreb creemos que la iglesia es una familia llamada a conocer a Dios,
                vivir el evangelio y servir con amor.
              </p>
              <p>
                Cada semana abrimos nuestras puertas para adorar, estudiar las Escrituras,
                orar unos por otros y crecer juntos. Seas nuevo en la fe o lleves años
                caminando con Cristo, queremos recibirte con alegría.
              </p>
              <div className="verse-card">
                <Quote aria-hidden="true" />
                <blockquote>“Para que en todo Él tenga la preeminencia.”</blockquote>
                <cite>Colosenses 1:18</cite>
              </div>
            </div>
          </div>
        </section>

        <section className="services section" id="servicios">
          <div className="shell">
            <div className="section-heading services-heading">
              <div>
                <p className="eyebrow eyebrow-light">Nos reunimos</p>
                <h2>Un tiempo para adorar, aprender y crecer juntos.</h2>
              </div>
              <p>
                Ven como eres. Te esperamos para compartir la Palabra y disfrutar de la
                comunión como una sola familia.
              </p>
            </div>

            <div className="service-grid">
              {serviceCards.map(({ icon: Icon, kicker, title, time, description, note }) => (
                <article className="service-card" key={title}>
                  <div className="service-icon"><Icon aria-hidden="true" /></div>
                  <p className="card-kicker">{kicker}</p>
                  <h3>{title}</h3>
                  <p className="service-time"><Clock3 aria-hidden="true" /> {time}</p>
                  <p>{description}</p>
                  <div className="service-note"><Sparkles aria-hidden="true" /> {note}</div>
                </article>
              ))}

              <article className="service-card service-card-accent">
                <div className="service-icon"><CalendarDays aria-hidden="true" /></div>
                <p className="card-kicker">Domingos especiales</p>
                <h3>Momentos del mes</h3>
                <div className="monthly-list">
                  <div>
                    <span>01</span>
                    <p><strong>Primer domingo</strong>Ayuno y oración desde las 9:00 a. m.; servicio a las 10:00 a. m.</p>
                  </div>
                  <div>
                    <span>✦</span>
                    <p><strong>Último domingo</strong>Santa Cena y koinonía después de nuestro tiempo de adoración.</p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="ministries section" id="ministerios">
          <div className="shell">
            <div className="section-heading section-heading-centered">
              <p className="eyebrow">Una iglesia en movimiento</p>
              <h2>Hay un lugar para crecer y servir.</h2>
              <p>
                Vivimos la fe en comunidad, cuidando a cada generación y compartiendo el
                amor de Jesús dentro y fuera de nuestras paredes.
              </p>
            </div>
            <div className="ministry-grid">
              {ministries.map(({ title, description, image, alt, icon: Icon }, index) => (
                <article className="ministry-card" key={title}>
                  <div className="ministry-image">
                    <img src={image} alt={alt} loading="lazy" />
                    <span><Icon aria-hidden="true" /></span>
                  </div>
                  <div className="ministry-copy">
                    <span className="ministry-number">0{index + 1}</span>
                    <h3>{title}</h3>
                    <p>{description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="pastors section" id="pastores">
          <div className="shell pastors-panel">
            <div className="pastors-image-wrap">
              <img
                src={assets.pastors}
                alt="Pastores Fernando Urbano Ramírez y Yazmín Ramírez"
                loading="lazy"
              />
              <span className="pastors-stamp"><HeartHandshake aria-hidden="true" /></span>
            </div>
            <div className="pastors-copy">
              <p className="eyebrow eyebrow-light">Nuestros pastores</p>
              <h2>Fernando Urbano Ramírez <span>&amp;</span> Yazmín Ramírez</h2>
              <p className="lead">
                Sirviendo a la familia Adhoreb con amor, fidelidad y un profundo compromiso
                con la Palabra de Dios.
              </p>
              <p>
                Su deseo es acompañar a cada persona a conocer más de Cristo, fortalecer su
                fe y vivir el evangelio en cada área de la vida.
              </p>
              <a className="text-link" href="mailto:adhoreb.sc@gmail.com">
                Escríbenos <ArrowRight size={18} aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        <section className="gallery section" id="galeria">
          <div className="shell">
            <div className="section-heading gallery-heading">
              <div>
                <p className="eyebrow">Nuestra comunidad</p>
                <h2>Momentos que vivimos juntos.</h2>
              </div>
              <p>Fe, aprendizaje, oración y alegría compartidos como familia.</p>
            </div>
            <div className="gallery-grid">
              {galleryImages.map((image, index) => (
                <button
                  className={`gallery-item gallery-item-${index + 1}`}
                  type="button"
                  key={image.src}
                  onClick={() => openGalleryImage(index)}
                  aria-label={`Ampliar imagen: ${image.caption}`}
                >
                  <img src={image.src} alt={image.alt} loading="lazy" />
                  <span>{image.caption}<ExternalLink size={16} aria-hidden="true" /></span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="visit section" id="visitanos">
          <div className="shell visit-panel">
            <div className="visit-image">
              <img src={assets.church} alt="Entrada de la Iglesia Bautista Adhoreb" loading="lazy" />
            </div>
            <div className="visit-copy">
              <p className="eyebrow">Te esperamos</p>
              <h2>Ven y sé parte de nuestra familia.</h2>
              <p>
                Será una alegría recibirte. Estamos ubicados en el corazón de San Cristóbal,
                al lado de Electrónica.
              </p>
              <address>
                <div>
                  <MapPin aria-hidden="true" />
                  <span><strong>Nuestra dirección</strong>Calle Modesto Díaz #5, San Cristóbal 92000,<br /> República Dominicana</span>
                </div>
                <div>
                  <Mail aria-hidden="true" />
                  <span><strong>Correo electrónico</strong><a href="mailto:adhoreb.sc@gmail.com">adhoreb.sc@gmail.com</a></span>
                </div>
              </address>
              <a className="button button-dark" href={mapUrl} target="_blank" rel="noreferrer">
                Abrir en Google Maps
                <ExternalLink size={18} aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="shell footer-grid">
          <div className="footer-brand">
            <img src={assets.icon} alt="" aria-hidden="true" />
            <div><strong>Iglesia Bautista Adhoreb</strong><span>Una familia centrada en Cristo.</span></div>
          </div>
          <div className="footer-column">
            <strong>Horarios</strong>
            <span>Miércoles · 6:30 p. m.</span>
            <span>Domingos · 10:00 a. m.</span>
          </div>
          <div className="footer-column">
            <strong>Encuéntranos</strong>
            <span>Modesto Díaz #5<br />San Cristóbal, R. D.</span>
          </div>
          <div className="footer-column footer-links">
            <strong>Conecta</strong>
            <a href={facebookUrl} target="_blank" rel="noreferrer">Facebook y transmisiones</a>
            <a href="#nosotros">Nosotros</a>
            <a href="#ministerios">Ministerios</a>
            <a href="#visitanos">Visítanos</a>
          </div>
        </div>
        <div className="shell footer-bottom">
          <span>© {new Date().getFullYear()} Iglesia Bautista Adhoreb.</span>
          <span>San Cristóbal, República Dominicana</span>
        </div>
      </footer>

      <dialog
        className="lightbox"
        ref={dialogRef}
        aria-label="Imagen ampliada de la galería"
        onClose={() => setSelectedImage(null)}
        onClick={(event) => {
          if (event.target === dialogRef.current) dialogRef.current?.close();
        }}
      >
        {selectedImage !== null && (
          <div className="lightbox-content">
            <button className="lightbox-close" type="button" onClick={() => dialogRef.current?.close()} aria-label="Cerrar imagen">
              <X aria-hidden="true" />
            </button>
            <img src={galleryImages[selectedImage].src} alt={galleryImages[selectedImage].alt} />
            <div className="lightbox-bar">
              <button type="button" onClick={() => changeGalleryImage(-1)} aria-label="Imagen anterior"><ArrowLeft aria-hidden="true" /></button>
              <p>{galleryImages[selectedImage].caption}</p>
              <button type="button" onClick={() => changeGalleryImage(1)} aria-label="Imagen siguiente"><ArrowRight aria-hidden="true" /></button>
            </div>
          </div>
        )}
      </dialog>
    </>
  );
}
